import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ReactComponent as CheckIcon} from 'assets/svg/check2.svg';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {Spinner} from 'components/ui-kit/Spinner';
import {ROUTE} from 'core/routes';
import {commonDataState$, remarkingDataState$} from 'models/products/detail';
import {formatDate} from 'utils/date';
import {toHex} from 'utils/string';

import {
    Chain,
    ChainArrow,
    ChainCode,
    ChainSection,
    CodeLink,
    Container,
    HistoryTable,
    Reason,
    SectionTitle,
    SpinnerContainer,
    StatusIcon,
    StatusList,
    StatusRow,
    TableWrapper,
} from './styled';

const getProductLink = (markingCode: string) =>
    `${generatePath(ROUTE.PRODUCTS_DETAIL, {
        id: toHex(`${null}§${markingCode}`),
    })}/remarking`;

export const Remarking = () => {
    const {t} = useTranslation();
    const {data: product} = useStore(commonDataState$);
    const {data, hasError, isLoading} = useStore(remarkingDataState$);

    if (isLoading) {
        return (
            <SpinnerContainer>
                <Spinner />
            </SpinnerContainer>
        );
    }

    if (hasError || !data) {
        return <Container>{t('pages.products.detail.remarking.unavailable')}</Container>;
    }

    return (
        <Container>
            <SectionTitle>{t('pages.products.detail.remarking.title')}</SectionTitle>

            <StatusList>
                {product?.replacesMarkingCodeId && (
                    <StatusRow>
                        <StatusIcon>
                            <Icon source={CheckIcon} width={9} height={7} />
                        </StatusIcon>
                        <span>
                            {t('pages.products.detail.remarking.received')}{' '}
                            <Link to={getProductLink(product.replacesMarkingCodeId)}>
                                {product.replacesMarkingCodeId}
                            </Link>
                        </span>
                    </StatusRow>
                )}

                {product?.replacedByMarkingCodeId && (
                    <StatusRow>
                        <StatusIcon>
                            <Icon source={CheckIcon} width={9} height={7} />
                        </StatusIcon>
                        <span>
                            {t('pages.products.detail.remarking.replaced')}{' '}
                            <Link to={getProductLink(product.replacedByMarkingCodeId)}>
                                {product.replacedByMarkingCodeId}
                            </Link>
                        </span>
                    </StatusRow>
                )}
            </StatusList>

            <ChainSection>
                <SectionTitle>{t('pages.products.detail.remarking.replacementChain')}</SectionTitle>
                <Chain>
                    {data.chain.map((code, index) => (
                        <React.Fragment key={code}>
                            {index > 0 && <ChainArrow>→</ChainArrow>}
                            <ChainCode
                                to={getProductLink(code)}
                                $current={code === product?.markingCode}
                            >
                                {code}
                            </ChainCode>
                        </React.Fragment>
                    ))}
                </Chain>
            </ChainSection>

            <TableWrapper>
                <HistoryTable>
                    <thead>
                        <tr>
                            <th>{t('pages.products.detail.remarking.oldCode')}</th>
                            <th>{t('pages.products.detail.remarking.newCode')}</th>
                            <th>{t('pages.products.detail.remarking.date')}</th>
                            <th>{t('pages.products.detail.remarking.document')}</th>
                            <th>{t('pages.products.detail.remarking.reason')}</th>
                            <th>{t('pages.products.detail.remarking.initiator')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((row) => (
                            <tr key={`${row.oldCode}-${row.newCode}`}>
                                <td>
                                    <CodeLink to={getProductLink(row.oldCode)}>
                                        {row.oldCode}
                                    </CodeLink>
                                </td>
                                <td>
                                    <CodeLink to={getProductLink(row.newCode)}>
                                        {row.newCode}
                                    </CodeLink>
                                </td>
                                <td>
                                    {row.operationTime
                                        ? formatDate(row.operationTime, 'dd.MM.yyyy HH:mm')
                                        : '—'}
                                </td>
                                <td>
                                    {row.reportId ? (
                                        <Link
                                            to={generatePath(ROUTE.DOCUMENTS_DETAIL, {
                                                id: row.reportId,
                                            })}
                                        >
                                            {row.reportId}
                                        </Link>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <td>
                                    {row.reason ? (
                                        <Reason $reason={row.reason}>
                                            {t(
                                                `pages.products.detail.remarking.reasons.${row.reason}`,
                                            )}
                                        </Reason>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <td>{row.initiator || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </HistoryTable>
            </TableWrapper>
        </Container>
    );
};
